import React, { useEffect, useState, useMemo } from "react";
import { getApiBase } from '../api';
import { useAuth } from '../components/AuthContext';

const EQUIPMENT_MAP = [
  { code: "DRY_VAN", label: "Dry Van" },
  { code: "REEFER", label: "Reefer (Refrigerated)" },
  { code: "FLATBED", label: "Flatbed" },
  { code: "STEP_DECK", label: "Step Deck" },
  { code: "LOWBOY", label: "Lowboy" },
  { code: "TANKER", label: "Tanker" },
  { code: "INTERMODAL", label: "Intermodal" },
  { code: "BOX_TRUCK", label: "Box Truck" },
  { code: "HOTSHOT", label: "Hotshot" },
  { code: "POWER_ONLY", label: "Power Only" },
];

export default function CarrierProfile() {
  const apiBase = useMemo(() => getApiBase(), []);
  const { carrierEmail } = useAuth();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedEquipment, setSelectedEquipment] = useState([]);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [searchRadius, setSearchRadius] = useState(100);
  const [smsEnabled, setSmsEnabled] = useState(true);
  const [notifLevel, setNotifLevel] = useState('ALL_UPDATES');
  const [notifyPickup, setNotifyPickup] = useState(true);
  const [notifyDelivery, setNotifyDelivery] = useState(true);
  const [notifyStatusChange, setNotifyStatusChange] = useState(true);
  const [notifyEta, setNotifyEta] = useState(false);
  const [notifyDocs, setNotifyDocs] = useState(false);
  const [notifyDigest, setNotifyDigest] = useState(false);
  const [notifChannels, setNotifChannels] = useState(['EMAIL']);

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carrierEmail]);

  async function fetchProfile() {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${apiBase}/carrier/profile`, {
        headers: { 'x-carrier-email': carrierEmail }
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to load profile');
      }
      const data = await res.json();
      const c = data.company;
      setCompany(c);
      setSelectedEquipment(c?.equipmentTypes || []);
      setPhone(c?.phone || '');
      setEmail(c?.email || '');
      setSearchRadius(c?.searchRadiusMiles || 100);
      setSmsEnabled(c?.smsEnabled !== false);
      setNotifLevel(c?.notificationLevel || 'ALL_UPDATES');
      setNotifyPickup(c?.notifyOnPickup !== false);
      setNotifyDelivery(c?.notifyOnDelivery !== false);
      setNotifyStatusChange(c?.notifyOnStatusChange !== false);
      setNotifyEta(c?.notifyOnEtaChange === true);
      setNotifyDocs(c?.notifyOnDocuments === true);
      setNotifyDigest(c?.notifyDailyDigest === true);
      setNotifChannels(c?.notificationChannels || ['EMAIL']);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${apiBase}/carrier/profile`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'x-carrier-email': carrierEmail
        },
        body: JSON.stringify({
          equipmentTypes: selectedEquipment,
          phone,
          email,
          searchRadiusMiles: searchRadius,
          smsEnabled,
          notificationLevel: notifLevel,
          notifyOnPickup: notifyPickup,
          notifyOnDelivery: notifyDelivery,
          notifyOnStatusChange: notifyStatusChange,
          notifyOnEtaChange: notifyEta,
          notifyOnDocuments: notifyDocs,
          notifyDailyDigest: notifyDigest,
          notificationChannels: notifChannels,
        })
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || 'Failed to save');
      }
      setSuccess('Profile saved successfully!');
      fetchProfile();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function toggleEquipment(type) {
    setSelectedEquipment(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  }

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading profile...</div>;

  return (
    <div style={{ maxWidth: 700, margin: '0 auto' }}>
      {/* Hero Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        borderRadius: '16px 16px 0 0',
        padding: '32px 32px 24px',
        color: 'white',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: 48, marginBottom: 8 }}>🚛</div>
      </div>

      <div style={{ background: 'white', borderRadius: '0 0 16px 16px', border: '1px solid #e5e7eb', borderTop: 'none', padding: '32px' }}>
        {error && (
          <div style={{ padding: 12, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, color: '#991b1b', marginBottom: 16 }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{ padding: 12, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, color: '#166534', marginBottom: 16 }}>
            {success}
          </div>
        )}

        {/* Company Info */}
        <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 16 }}>Company Information</h2>
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 4 }}>Company Name</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{company?.name || '—'}</div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
          <div>
            <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 4 }}>MC Number</div>
            <div style={{ fontWeight: 600 }}>{company?.mcNumber || '—'}</div>
          </div>
          <div>
            <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 4 }}>DOT Number</div>
            <div style={{ fontWeight: 600 }}>{company?.dotNumber || '—'}</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
          <div>
            <label style={{ display: 'block', fontSize: 14, color: '#6b7280', marginBottom: 4 }}>Phone</label>
            <input
              type="tel" value={phone} onChange={e => setPhone(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 15, boxSizing: 'border-box' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: 14, color: '#6b7280', marginBottom: 4 }}>Email</label>
            <input
              type="email" value={email} onChange={e => setEmail(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 15, boxSizing: 'border-box' }}
            />
          </div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 4 }}>Status</div>
          <span style={{
            padding: '4px 12px', borderRadius: 999, fontSize: 13, fontWeight: 700,
            background: company?.status === 'ACTIVE' ? '#dcfce7' : '#fef3c7',
            color: company?.status === 'ACTIVE' ? '#166534' : '#92400e'
          }}>
            {company?.status || 'PENDING'}
          </span>
        </div>

        {/* Equipment Types */}
        <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 8, marginTop: 32 }}>🚚 Equipment Types</h2>
        <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>
          Select all the equipment types your company operates. This determines which loads you can see and book.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 32 }}>
          {EQUIPMENT_MAP.map(({ code, label }) => {
            const selected = selectedEquipment.includes(code);
            return (
              <div
                key={code}
                onClick={() => toggleEquipment(code)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '14px 16px', borderRadius: 10, cursor: 'pointer',
                  border: selected ? '2px solid #dc2626' : '2px solid #e5e7eb',
                  background: selected ? '#fef2f2' : 'white',
                  fontWeight: selected ? 700 : 500,
                  transition: 'all 0.15s ease'
                }}
              >
                <div style={{
                  width: 22, height: 22, borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: selected ? '#dc2626' : 'white', border: selected ? 'none' : '2px solid #d1d5db',
                  color: 'white', fontSize: 14, fontWeight: 900
                }}>
                  {selected ? '✓' : ''}
                </div>
                {label}
              </div>
            );
          })}
        </div>

        {/* AI Backhaul Settings */}
        <h2 style={{ fontSize: 20, fontWeight: 800, marginBottom: 8, marginTop: 32 }}>🤖 AI Backhaul Settings</h2>
        <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 16 }}>
          Configure how RoadOptix finds return loads near your delivery destinations.
        </p>

        <div style={{ background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20, marginBottom: 16 }}>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
              Search Radius: <span style={{ color: '#dc2626', fontSize: 18 }}>{searchRadius} miles</span>
            </label>
            <input
              type="range" min={25} max={500} step={25} value={searchRadius}
              onChange={e => setSearchRadius(Number(e.target.value))}
              style={{ width: '100%', accentColor: '#dc2626' }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
              <span>25 mi</span>
              <span>250 mi</span>
              <span>500 mi</span>
            </div>
            <p style={{ fontSize: 13, color: '#64748b', marginTop: 8 }}>
              We'll look for available loads within this distance of your delivery point.
            </p>
          </div>

          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '16px 0', borderTop: '1px solid #e2e8f0'
          }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>📱 SMS Load Alerts</div>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
                Get text messages when AI finds a backhaul match
              </div>
            </div>
            <div
              onClick={() => setSmsEnabled(!smsEnabled)}
              style={{
                width: 52, height: 28, borderRadius: 14, cursor: 'pointer',
                background: smsEnabled ? '#dc2626' : '#d1d5db',
                position: 'relative', transition: 'background 0.2s ease'
              }}
            >
              <div style={{
                width: 24, height: 24, borderRadius: 12, background: 'white',
                position: 'absolute', top: 2,
                left: smsEnabled ? 26 : 2,
                transition: 'left 0.2s ease',
                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
              }} />
            </div>
          </div>
          {smsEnabled && (
            <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
              By enabling SMS alerts you consent to receive text messages about load matches.
              Msg & data rates may apply. Reply STOP to opt out.{' '}
              <a href="/sms-consent" style={{ color: '#dc2626' }}>SMS Terms</a>
            </p>
          )}
        </div>

        {/* NOTIFICATION PREFERENCES */}
        <div style={{ marginTop: 20, padding: 18, border: "1px solid #e5e7eb", borderRadius: 12, background: "white" }}>
          <h3 style={{ margin: "0 0 14px", fontWeight: 800, fontSize: 16 }}>🔔 Notification Preferences</h3>
          <p style={{ margin: "0 0 14px", fontSize: 13, color: "#6b7280" }}>
            Control how and when you receive updates about your loads.
          </p>

          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 13, fontWeight: 700, display: "block", marginBottom: 6 }}>Update Level</label>
            <select value={notifLevel} onChange={e => setNotifLevel(e.target.value)}
              style={{ padding: 10, borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14, width: "100%" }}>
              <option value="ALL_UPDATES">All Updates — Every status change, ETA update, document</option>
              <option value="MILESTONES_ONLY">Milestones Only — Pickup, delivery, and major events</option>
              <option value="DAILY_DIGEST">Daily Digest — One summary per day</option>
              <option value="MINIMAL">Minimal — Only critical alerts</option>
            </select>
          </div>

          <div style={{ marginBottom: 14 }}>
            <label style={{ fontSize: 13, fontWeight: 700, display: "block", marginBottom: 8 }}>Notify Me When:</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {[
                { label: "📦 Load Picked Up", value: notifyPickup, setter: setNotifyPickup },
                { label: "✅ Load Delivered", value: notifyDelivery, setter: setNotifyDelivery },
                { label: "🔄 Status Changes", value: notifyStatusChange, setter: setNotifyStatusChange },
                { label: "⏱ ETA Changes", value: notifyEta, setter: setNotifyEta },
                { label: "📄 New Documents", value: notifyDocs, setter: setNotifyDocs },
                { label: "📊 Daily Summary", value: notifyDigest, setter: setNotifyDigest },
              ].map(item => (
                <label key={item.label} style={{
                  display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
                  borderRadius: 8, border: "1px solid #e5e7eb", cursor: "pointer",
                  background: item.value ? "#eff6ff" : "white"
                }}>
                  <input type="checkbox" checked={item.value} onChange={() => item.setter(!item.value)}
                    style={{ width: 18, height: 18 }} />
                  <span style={{ fontSize: 14, fontWeight: item.value ? 700 : 400 }}>{item.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label style={{ fontSize: 13, fontWeight: 700, display: "block", marginBottom: 8 }}>Preferred Channels:</label>
            <div style={{ display: "flex", gap: 8 }}>
              {[
                { code: "EMAIL", label: "📧 Email" },
                { code: "SMS", label: "📱 SMS" },
                { code: "PORTAL", label: "🖥 Portal Only" },
              ].map(ch => {
                const active = notifChannels.includes(ch.code);
                return (
                  <button key={ch.code} type="button" onClick={() => {
                    if (active) setNotifChannels(notifChannels.filter(c => c !== ch.code));
                    else setNotifChannels([...notifChannels, ch.code]);
                  }}
                    style={{
                      padding: "8px 16px", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer",
                      border: active ? "none" : "1px solid #e5e7eb",
                      background: active ? "#2563eb" : "white",
                      color: active ? "white" : "#374151",
                    }}>
                    {ch.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>


        {/* Save Button */}
        <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
          <button
            onClick={handleSave}
            disabled={saving}
            style={{
              flex: 1, padding: '14px 24px',
              background: saving ? '#9ca3af' : 'linear-gradient(135deg, #dc2626, #b91c1c)',
              color: 'white', border: 'none', borderRadius: 10,
              fontWeight: 800, fontSize: 16, cursor: saving ? 'not-allowed' : 'pointer'
            }}


          >
            {saving ? 'Saving...' : '💾 Save Profile'}
          </button>
          <button
            onClick={fetchProfile}
            style={{
              padding: '14px 24px', background: 'white', color: '#374151',
              border: '2px solid #e5e7eb', borderRadius: 10,
              fontWeight: 700, fontSize: 16, cursor: 'pointer'
            }}
          >
            ↻ Reload
          </button>
        </div>
      </div>
    </div>
  );
}
